import React, { useState, useEffect, useCallback, useRef } from 'react';
import { db, auth } from '../firebase';
import { collection, getDocs, doc, runTransaction, serverTimestamp, query, updateDoc, where, setDoc, deleteDoc, getDoc } from 'firebase/firestore';
import '../GestionUsuarios.css';
import EditModal from './EditModal';
import { abrirReporteCotizacion } from './SicReporteCotizacion'; 

// Componente para la búsqueda de clientes en un modal
const ClienteSearchModal = ({ onClose, onSelect, isAdmin }) => {
    const [clientes, setClientes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingCliente, setEditingCliente] = useState(null);

    const fetchClientes = useCallback(async () => {
        setLoading(true);
        try {
            const clientesCollection = collection(db, 'SicTablaClientes');
            const clientesSnapshot = await getDocs(clientesCollection);
            const clientesList = clientesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setClientes(clientesList);
        } catch (error) {
            console.error("Error al obtener clientes: ", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchClientes();
    }, [fetchClientes]);

    const filteredClientes = clientes.filter(cliente =>
        cliente.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cliente.cedula?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleSelect = (cliente) => {
        onSelect(cliente);
        onClose();
    };

    const handleEdit = (cliente) => {
        setEditingCliente(cliente);
        setIsEditModalOpen(true);
    };

    const handleDelete = async (cliente) => {
        if (!cliente || !cliente.id) return;
        const confirmado = window.confirm(`¿Está seguro que desea eliminar al cliente: ${cliente.nombre} (${cliente.id})?`);
        if (!confirmado) return;
        try {
            await deleteDoc(doc(db, 'SicTablaClientes', cliente.id));
            await fetchClientes();
            alert(`✅ Cliente ${cliente.nombre} eliminado correctamente.`);
        } catch (error) {
            console.error('Error al eliminar cliente:', error);
            alert('❌ Ocurrió un error al eliminar el cliente.');
        }
    };

    const handleCreate = () => {
        setEditingCliente(null);
        setIsEditModalOpen(true);
    };

    const handleSaveEdit = async (recordData) => {
        try {
            const currentUserEmail = auth.currentUser?.email || '';
            if (recordData && recordData.id) {
                // Edición de cliente existente
                const recordRef = doc(db, 'SicTablaClientes', recordData.id);
                await updateDoc(recordRef, {
                    nombre: recordData.nombre || '',
                    correo: recordData.correo || '',
                    telefonoOficina: recordData.telefonoOficina || '',
                    telefonoCelular: recordData.telefonoCelular || '',
                    responsable: currentUserEmail,
                    fechaModificacion: serverTimestamp(),
                });
            } else {
                // Creación de cliente nuevo con ID = cédula
                const cedula = (recordData.cedula || '').trim();
                if (!cedula) {
                    throw new Error('La cédula es obligatoria para crear el cliente.');
                }
                const nuevoDocRef = doc(db, 'SicTablaClientes', cedula);
                await setDoc(nuevoDocRef, {
                    cedula: cedula,
                    nombre: recordData.nombre || '',
                    correo: recordData.correo || '',
                    telefonoOficina: recordData.telefonoOficina || '',
                    telefonoCelular: recordData.telefonoCelular || '',
                    responsable: currentUserEmail,
                    fechaCreacion: serverTimestamp(),
                    fechaModificacion: serverTimestamp(),
                }, { merge: true });
            }
            await fetchClientes(); // Recargar la lista de clientes
            setIsEditModalOpen(false);
            setEditingCliente(null);
        } catch (error) {
            console.error("Error al guardar/actualizar el cliente: ", error);
            alert("Error al guardar los cambios del cliente.");
        }
    };

    return (
        <>
            <div className="modal-overlay" onClick={onClose}>
                <div className="modal-content" onClick={e => e.stopPropagation()} style={{ width: '80%', maxWidth: '900px' }}>
                    <div className="modal-header">
                        <h2>Buscar Cliente</h2>
                        <button onClick={onClose} className="modal-close">×</button>
                    </div>
                    <div className="modal-body">
                        <div className="search-container" style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0 1rem', marginBottom: '1rem' }}>
                            <input
                                type="text"
                                placeholder="Buscar por nombre o cédula..."
                                className="form-control"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{ flex: 1, padding: '0.75rem' }}
                            />
                            <div className="tooltip-container">
                            <button 
                                    className="btn btn-primary icon-btn"
                                    onClick={handleCreate}
                                    style={{ 
                                        padding: '0.5rem 1rem',
                                        backgroundColor: '#28a745', 
                                        color: 'white',
                                        fontSize: '1.1rem'
                                    }}
                                >
                                    ➕
                                </button>
                                <span className="tooltip-text">Crear Cliente</span>
                            </div>
                        </div>
                        {loading ? <p>Cargando...</p> : (
                            <div className="table-responsive" style={{ maxHeight: '400px' }}>
                                <table className="usuarios-table" style={{ width: '100%' }}>
                                    <thead>
                                        <tr>
                                            <th style={{ width: '65%' }}>Nombre</th>
                                            <th style={{ width: '15%' }}>Cédula</th>
                                            <th style={{ width: '20%' }}>Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredClientes.map(cliente => (
                                            <tr key={cliente.id}>
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={cliente.nombre || ''}
                                                        className="form-control"
                                                        readOnly
                                                        style={{ 
                                                            backgroundColor: '#f8f9fa', 
                                                            border: '1px solid #dee2e6',
                                                            width: '100%'
                                                        }}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={cliente.cedula || ''}
                                                        className="form-control"
                                                        readOnly
                                                        style={{ 
                                                            backgroundColor: '#f8f9fa', 
                                                            border: '1px solid #dee2e6',
                                                            width: '100%'
                                                        }}
                                                    />
                                                </td>
                                                <td className="actions-cell">
                                                    <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                                                        <div className="tooltip-container">
                                                            <button 
                                                                className="icon-btn success" 
                                                                onClick={() => handleSelect(cliente)}
                                                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '5px' }}
                                                            >
                                                                ✅
                                                            </button>
                                                            <span className="tooltip-text">Seleccionar</span>
                                                        </div>
                                                        <div className="tooltip-container">
                                                            <button 
                                                                className="icon-btn warning" 
                                                                onClick={() => handleEdit(cliente)}
                                                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '5px' }}
                                                            >
                                                                ✏️
                                                            </button>
                                                            <span className="tooltip-text">Editar</span>
                                                        </div>
                                                        {isAdmin && (
                                                            <div className="tooltip-container">
                                                                <button
                                                                    className="icon-btn danger"
                                                                    onClick={() => handleDelete(cliente)}
                                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '5px' }}
                                                                >
                                                                    🗑️
                                                                </button>
                                                                <span className="tooltip-text">Eliminar</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {isEditModalOpen && (
                <EditModal
                    record={editingCliente}
                    onClose={() => setIsEditModalOpen(false)}
                    onSave={handleSaveEdit}
                    type="Cliente"
                />
            )}
        </>
    );
};

// Modal para ingresar el monto del cálculo inverso
const PriceCalculatorModal = ({ onClose, onCalculate, currencySymbol }) => {
    const [totalValue, setTotalValue] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();
        const value = parseFloat(totalValue);
        if (!isNaN(value) && value >= 0) {
            onCalculate(value);
        } else {
            alert("Por favor, ingrese un valor numérico válido.");
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()} style={{ width: 'auto', maxWidth: '400px' }}>
                <div className="modal-header">
                    <h2>Calcular Precio Unitario</h2>
                    <button onClick={onClose} className="modal-close">×</button>
                </div>
                <form onSubmit={handleSubmit} className="modal-body">
                    <div className="form-group">
                        <label htmlFor="total-value-input">Ingrese el monto total deseado para la línea:</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <span style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>{currencySymbol}</span>
                            <input
                                id="total-value-input"
                                type="number"
                                value={totalValue}
                                onChange={(e) => setTotalValue(e.target.value)}
                                className="form-control"
                                placeholder="Ej: 100.00"
                                step="0.01"
                                autoFocus
                            />
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
                        <button type="submit" className="btn btn-primary">Calcular</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const getTodayFormatted = () => {
    const fecha = new Date();
    const dias = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
    const meses = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
    return `${dias[fecha.getDay()]}, ${fecha.getDate()} de ${meses[fecha.getMonth()]} de ${fecha.getFullYear()}`;
};

const SicCotizaciones = () => {
    const [encabezado, setEncabezado] = useState({
        numCotizacion: '',
        fecha: new Date(),
        cliente: '',
        atencion: '',
        correo: '',
        telefonoOficina: '9999-9999',
        telefonoCelular: '9999-9999',
        moneda: 'Colones',
        tipoCambio: 1,
        porcentajeUtilidad: 15,
        formaDePago: 'Contado',
        plazo: 0,
        exento: false,
        imprimeMontos: true,
        subtotaliza: true,
        iva: 13,
        observaciones: '',
        //firma: true,
        hechopor: '',
    });

    const [detalles, setDetalles] = useState([{ orden: 1, cantidad: 1, detalle: 'Descripción del artículo/servicio', precioUnitario: 0, subtotal: 0, impuesto: 0 }]);
    const [totales, setTotales] = useState({ subtotal: 0, impuesto: 0, total: 0 });
    const [usuarios, setUsuarios] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sortOrder, setSortOrder] = useState('asc');
    const [isClienteModalOpen, setIsClienteModalOpen] = useState(false);
    const [error, setError] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
    const [allCotizaciones, setAllCotizaciones] = useState([]);

    const [isCalculatorModalOpen, setIsCalculatorModalOpen] = useState(false);
    const [calculatingIndex, setCalculatingIndex] = useState(null);
    // Estado y lógica para fijar la barra como fallback
    const [isToolbarFixed, setIsToolbarFixed] = useState(false);
    const [toolbarHeight, setToolbarHeight] = useState(0);
    const [headerHeight, setHeaderHeight] = useState(0);
    const toolbarRef = useRef(null);

    useEffect(() => {
        const scrollContainer = document.querySelector('.main-content');
        const appHeader = document.querySelector('.app-header');
        const updateHeaderHeight = () => setHeaderHeight(appHeader ? appHeader.offsetHeight || 0 : 0);

        const handleScrollAny = () => {
            const scrolledMain = scrollContainer ? (scrollContainer.scrollTop || 0) : 0;
            const scrolledWindow = window.pageYOffset || document.documentElement.scrollTop || 0;
            if (toolbarRef.current) {
                const currentHeight = toolbarRef.current.offsetHeight || 0;
                if (currentHeight && currentHeight !== toolbarHeight) setToolbarHeight(currentHeight);
            }
            setIsToolbarFixed(scrolledMain > 0 || scrolledWindow > 0);
        };

        updateHeaderHeight();
        handleScrollAny();

        scrollContainer?.addEventListener('scroll', handleScrollAny, { passive: true });
        window.addEventListener('scroll', handleScrollAny, { passive: true });
        window.addEventListener('resize', updateHeaderHeight, { passive: true });

        return () => {
            scrollContainer?.removeEventListener('scroll', handleScrollAny);
            window.removeEventListener('scroll', handleScrollAny);
            window.removeEventListener('resize', updateHeaderHeight);
        };
    }, [toolbarHeight]);

    const fetchInitialData = useCallback(async () => {
        try {
            const usersSnapshot = await getDocs(collection(db, 'usuarios'));
            const usersList = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setUsuarios(usersList);

            const currentUserEmail = auth.currentUser?.email;
            if (currentUserEmail) {
                const currentUser = usersList.find(u => u.email === currentUserEmail);
                if (currentUser) {
                    setEncabezado(prev => ({ ...prev, hechopor: currentUser.nombre }));
                }
            }

            const paramsSnapshot = await getDocs(collection(db, 'Parametros'));
            if (!paramsSnapshot.empty) {
                const paramsData = paramsSnapshot.docs[0].data();
                setEncabezado(prev => ({ 
                    ...prev, 
                    iva: paramsData.impuesto || 13
                }));
            }
        } catch (err) {
            console.error("Error fetching initial data:", err);
            setError("No se pudieron cargar los datos iniciales (usuarios, IVA).");
        }
    }, []);

    useEffect(() => {
        fetchInitialData();
    }, [fetchInitialData]);

    // Efecto para recalcular los detalles cuando cambia el % de utilidad
    useEffect(() => {
        const tipoCambio = parseFloat(encabezado.tipoCambio) || 1;
        const porcentajeUtilidadDecimal = (parseFloat(encabezado.porcentajeUtilidad) || 0) / 100;

        setDetalles(prevDetalles => 
            prevDetalles.map(detalle => {
                const cantidad = detalle.cantidad || 0;
                const precio = detalle.precioUnitario || 0;
                
                // 1. Calcula el subtotal base (sin utilidad ni tipo de cambio)
                let subtotalBase = cantidad * precio;

                // 2. Aplica el tipo de cambio si la moneda es Colones
                if (encabezado.moneda === 'Colones' && tipoCambio > 1) {
                    subtotalBase *= tipoCambio;
                }

                // 3. Aplica la utilidad sobre el subtotal ya convertido
                const subtotalConUtilidad = subtotalBase * (1 + porcentajeUtilidadDecimal);

                // 4. Calcula el IVA
                const impuestoLinea = encabezado.exento ? 0 : subtotalConUtilidad * (encabezado.iva / 100);

                // 5. El subtotal final incluye el IVA
                return {
                    ...detalle,
                    subtotal: subtotalConUtilidad + impuestoLinea,
                    impuesto: impuestoLinea
                };
            })
        );
    }, [encabezado.porcentajeUtilidad, encabezado.tipoCambio, encabezado.moneda, encabezado.iva, encabezado.exento]);

    useEffect(() => {
        // El subtotal general ya incluye el IVA de cada línea
        const subtotalGeneral = detalles.reduce((acc, item) => acc + (item.subtotal || 0), 0);
        const impuestoTotal = detalles.reduce((acc, item) => acc + (item.impuesto || 0), 0);
        
        setTotales({
            subtotal: subtotalGeneral - impuestoTotal, // Subtotal sin IVA para mostrarlo separado
            impuesto: impuestoTotal,
            total: subtotalGeneral, // Total con IVA incluido
        });
    }, [detalles, encabezado.iva, encabezado.exento, encabezado.moneda, encabezado.tipoCambio]);

    const handleEncabezadoChange = (e) => {
        const { name, value, type, checked } = e.target;
        const val = type === 'checkbox' ? checked : value;

        if (name === 'telefonoOficina' || name === 'telefonoCelular') {
            const digitsOnly = value.replace(/\D/g, '');
            let formatted = digitsOnly;
            if (digitsOnly.length > 4) {
                formatted = `${digitsOnly.substring(0, 4)}-${digitsOnly.substring(4, 8)}`;
            }
            setEncabezado(prev => ({ ...prev, [name]: formatted }));
        } else {
            setEncabezado(prev => ({ ...prev, [name]: val }));
        }
    };

    const handleClienteSelect = (cliente) => {
        setEncabezado(prev => ({
            ...prev,
            cliente: cliente.nombre,
            atencion: cliente.nombre,
            correo: cliente.correo || '',
            telefonoOficina: cliente.telefonoOficina || cliente.telefono || '',
            telefonoCelular: cliente.telefonoCelular || ''
        }));
    };

    const handleDetalleChange = (index, e) => {
        const { name, value } = e.target;
        const nuevosDetalles = [...detalles];
        const valorNumerico = name === 'cantidad' || name === 'precioUnitario' ? parseFloat(value) || 0 : value;
        nuevosDetalles[index][name] = valorNumerico;

        const cantidad = nuevosDetalles[index].cantidad || 0;
        const precio = nuevosDetalles[index].precioUnitario || 0;
        
        // Obtener el porcentaje de utilidad del encabezado y convertirlo a decimal
        const porcentajeUtilidadDecimal = (parseFloat(encabezado.porcentajeUtilidad) || 0) / 100;
        const tipoCambio = parseFloat(encabezado.tipoCambio) || 1;
        
        // 1. Calcular subtotal base
        let subtotalBase = cantidad * precio;
        
        // 2. Aplicar tipo de cambio si es Colones
        if (encabezado.moneda === 'Colones' && tipoCambio > 1) {
            subtotalBase *= tipoCambio;
        }
        
        // 3. Aplicar utilidad
        const subtotalConUtilidad = subtotalBase * (1 + porcentajeUtilidadDecimal);
        
        // 4. Calcular IVA sobre el subtotal con utilidad
        const impuestoLinea = encabezado.exento ? 0 : subtotalConUtilidad * (encabezado.iva / 100);
        
        // 5. El subtotal final incluye el IVA
        nuevosDetalles[index].subtotal = subtotalConUtilidad + impuestoLinea;
        nuevosDetalles[index].impuesto = impuestoLinea;

        setDetalles(nuevosDetalles);
    };

    const openCalculatorModal = (index) => {
        setCalculatingIndex(index);
        setIsCalculatorModalOpen(true);
    };

    const handleCalculatePrice = (totalValue) => {
        if (calculatingIndex === null) return;

        const currentDetail = detalles[calculatingIndex];
        const cantidad = currentDetail.cantidad || 0;

        if (cantidad <= 0) {
            alert("La cantidad de la línea debe ser mayor a cero para calcular el precio unitario.");
            setIsCalculatorModalOpen(false);
            return;
        }

        const tc = parseFloat(encabezado.tipoCambio) || 1;
        const porcentajeUtilidadDecimal = (parseFloat(encabezado.porcentajeUtilidad) || 0) / 100;

        const utilidadFactor = 1 + porcentajeUtilidadDecimal;
        const ivaFactor = 1 + (encabezado.exento ? 0 : (parseFloat(encabezado.iva) || 0) / 100);
        
        const subtotalSinUtilidadConIva = totalValue / utilidadFactor;
        const subtotalBase = subtotalSinUtilidadConIva / ivaFactor;

        let calculatedPrecioUnitario = subtotalBase / (cantidad * tc);

        // Recalcular el impuesto de la línea para mantener coherencia
        let baseRecalculo = cantidad * calculatedPrecioUnitario;
        if (encabezado.moneda === 'Colones' && tc > 1) {
            baseRecalculo *= tc;
        }
        const subtotalConUtilidadRecalculo = baseRecalculo * utilidadFactor;
        const impuestoRecalculado = encabezado.exento ? 0 : subtotalConUtilidadRecalculo * (parseFloat(encabezado.iva) / 100);

        const nuevosDetalles = [...detalles];
        nuevosDetalles[calculatingIndex] = {
            ...currentDetail,
            precioUnitario: parseFloat(calculatedPrecioUnitario.toFixed(4)),
            subtotal: totalValue,
            impuesto: impuestoRecalculado
        };
        setDetalles(nuevosDetalles);

        setIsCalculatorModalOpen(false);
        setCalculatingIndex(null);
    };

    const handleOrdenChange = (index, nuevoOrden) => {
        const nuevosDetalles = [...detalles];
        nuevosDetalles[index].orden = parseInt(nuevoOrden, 10) || 0;
        setDetalles(nuevosDetalles);
    };

    const sortDetalles = () => {
        const detallesOrdenados = [...detalles].sort((a, b) => a.orden - b.orden);
        setDetalles(detallesOrdenados);
    };

    const handleSortByOrden = () => {
        const newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
        const detallesOrdenados = [...detalles].sort((a, b) => {
            if (newSortOrder === 'asc') {
                return a.orden - b.orden;
            } else {
                return b.orden - a.orden;
            }
        });
        setDetalles(detallesOrdenados);
        setSortOrder(newSortOrder);
    };

    const addDetalle = () => {
        const nuevoOrden = detalles.length > 0 ? Math.max(...detalles.map(d => d.orden)) + 1 : 1;
        const cantidad = 1;
        const precio = 0;
        const tipoCambio = parseFloat(encabezado.tipoCambio) || 1;
        const porcentajeUtilidadDecimal = (parseFloat(encabezado.porcentajeUtilidad) || 0) / 100;
        let subtotalBase = cantidad * precio;
        if (encabezado.moneda === 'Colones' && tipoCambio > 1) {
            subtotalBase *= tipoCambio;
        }
        const subtotalConUtilidad = subtotalBase * (1 + porcentajeUtilidadDecimal);
        const impuestoLinea = encabezado.exento ? 0 : subtotalConUtilidad * (encabezado.iva / 100);
        setDetalles([...detalles, { orden: nuevoOrden, cantidad: 1, detalle: '', precioUnitario: 0, subtotal: subtotalConUtilidad + impuestoLinea, impuesto: impuestoLinea }]);
    };

    const removeDetalle = (index) => {
        const nuevosDetalles = detalles.filter((_, i) => i !== index);
        const detallesReordenados = nuevosDetalles.map((d, i) => ({ ...d, orden: i + 1 }));
        setDetalles(detallesReordenados);
    };

    const handleSave = async () => {
        setLoading(true);
        setError('');

        if (!encabezado.cliente) {
            setError("El campo 'Cliente' es obligatorio.");
            setLoading(false);
            return;
        }

        if (detalles.some(d => !d.detalle || d.cantidad <= 0)) {
            setError("Todas las líneas de detalle deben tener una descripción y una cantidad mayor a cero.");
            setLoading(false);
            return;
        }

        const currentUserEmail = auth.currentUser?.email || '';

        if (isEditing) {
            try {
                const numCotizacionString = encabezado.numCotizacion;
                if (!numCotizacionString) {
                    throw new Error("No hay un número de cotización para actualizar.");
                }

                await runTransaction(db, async (transaction) => {
                    const encabezadoRef = doc(db, 'SicCotizaEncabezado', numCotizacionString);
                    const encabezadoToSave = {
                        ...encabezado,
                        subtotal: totales.subtotal,
                    fecha: encabezado.fecha instanceof Date ? encabezado.fecha : serverTimestamp(),
                        impuesto: totales.impuesto,
                        total: totales.total,
                        fechaModificacion: serverTimestamp(),
                        actualizadaPor: currentUserEmail,
                        fechaActualizada: serverTimestamp(),
                    };
                    transaction.update(encabezadoRef, encabezadoToSave);

                    const detallesQuery = query(collection(db, 'SicCotizaDetalles'), where('numCotizacion', '==', numCotizacionString));
                    const detallesSnapshot = await getDocs(detallesQuery);
                    detallesSnapshot.forEach(doc => transaction.delete(doc.ref));

                    detalles.forEach((detalle, index) => {
                        const detalleId = `${numCotizacionString}-${index + 1}`;
                        const detalleRef = doc(db, 'SicCotizaDetalles', detalleId);
                        const detalleToSave = { 
                            ...detalle, 
                            numCotizacion: numCotizacionString,
                            subtotal: detalle.subtotal,
                            impuesto: detalle.impuesto,
                            totalLinea: detalle.subtotal || 0
                        };
                        transaction.set(detalleRef, detalleToSave);
                    });
                });

                alert(`✅ Cotización #${numCotizacionString} actualizada exitosamente.`);
                await fetchAllCotizaciones(); // Actualizar lista para navegación

            } catch (err) {
                console.error("Error al actualizar la cotización: ", err);
                setError(`Error al actualizar: ${err.message}`);
                alert(`Error al actualizar la cotización: ${err.message}`);
            } finally {
                setLoading(false);
            }
            return;
        }

        try {
            const paramsQuery = query(collection(db, 'Parametros'));
            const paramsSnapshot = await getDocs(paramsQuery);
            if (paramsSnapshot.empty) {
                throw new Error("No se encontró ningún documento de parámetros.");
            }
            const paramRef = paramsSnapshot.docs[0].ref;

            const cotizacionGuardadaId = await runTransaction(db, async (transaction) => {
                const paramDoc = await transaction.get(paramRef);

                const currentNum = paramDoc.data().numCotizacion || 0;
                const newNumCotizacion = currentNum + 1;
                const numCotizacionString = String(newNumCotizacion).padStart(4, '0');

                transaction.update(paramRef, { numCotizacion: newNumCotizacion });

                const encabezadoRef = doc(db, 'SicCotizaEncabezado', numCotizacionString);
                const encabezadoToSave = {
                    ...encabezado,
                    numCotizacion: numCotizacionString,
                    subtotal: totales.subtotal,
                    impuesto: totales.impuesto,
                    total: totales.total,
                    fecha: serverTimestamp(),
                    responsable: currentUserEmail,
                };
                transaction.set(encabezadoRef, encabezadoToSave);

                detalles.forEach((detalle, index) => {
                    const detalleId = `${numCotizacionString}-${index + 1}`;
                    const detalleRef = doc(db, 'SicCotizaDetalles', detalleId);
                    const detalleToSave = {
                        ...detalle,
                        numCotizacion: numCotizacionString,
                        moneda: encabezado.moneda,
                        tipoCambio: encabezado.tipoCambio,
                        subtotal: detalle.subtotal,
                        impuesto: detalle.impuesto,
                        totalLinea: detalle.subtotal
                    };
                    transaction.set(detalleRef, detalleToSave);
                });

                return numCotizacionString;
            });

            alert(`✅ Cotización #${cotizacionGuardadaId} guardada exitosamente.`);
            setEncabezado(prev => ({ ...prev, numCotizacion: cotizacionGuardadaId }));
            setIsEditing(true);
            await fetchAllCotizaciones(); // Actualizar lista para navegación

        } catch (err) {
            console.error("Error al guardar la cotización: ", err);
            setError(`Error al guardar: ${err.message}`);
            alert(`Error al guardar la cotización: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleNew = () => {
        setEncabezado(prev => ({
            ...prev,
            numCotizacion: '',
            cliente: '',
            atencion: '',
            correo: '',
            telefonoOficina: '9999-9999',
            telefonoCelular: '9999-9999',
            moneda: 'Colones',
            tipoCambio: 1,
            formaDePago: 'Contado',
            plazo: 0,
            exento: false,
            observaciones: '',
            //firma: true,
        }));
        setDetalles([{ orden: 1, cantidad: 1, detalle: 'Descripción del artículo/servicio', precioUnitario: 0, subtotal: 0, impuesto: 0 }]);
        setIsEditing(false);
        setError('');
        setSearchTerm('');
    };

    const handleDuplicate = () => {
        if (!isEditing) {
            alert('Debe estar en una cotización existente para poder duplicarla.');
            return;
        }
        setEncabezado(prev => ({ ...prev, numCotizacion: '', fecha: new Date() }));
        setIsEditing(false);
        setError('');
        alert('Cotización duplicada. Ahora puede modificarla y guardarla como una nueva.');
    };

    // Cargar todas las cotizaciones para navegación
    const fetchAllCotizaciones = useCallback(async () => {
        try {
            const encabezadosSnapshot = await getDocs(collection(db, 'SicCotizaEncabezado'));
            const cotizacionesList = encabezadosSnapshot.docs.map(doc => ({
                id: doc.id,
                numCotizacion: doc.id,
                cliente: doc.data().cliente || '',
                ...doc.data()
            })).sort((a, b) => {
                const numA = parseInt(a.numCotizacion) || 0;
                const numB = parseInt(b.numCotizacion) || 0;
                return numA - numB;
            });
            setAllCotizaciones(cotizacionesList);
        } catch (error) {
            console.error("Error al cargar cotizaciones:", error);
        }
    }, []);

    useEffect(() => {
        fetchAllCotizaciones();
    }, [fetchAllCotizaciones]);

    // Cargar una cotización específica
    const loadCotizacion = async (numCotizacion) => {
        if (!numCotizacion) return;
        setLoading(true);
        setError('');
        try {
            const encabezadoRef = doc(db, 'SicCotizaEncabezado', numCotizacion);
            const encabezadoDoc = await getDoc(encabezadoRef);
            
            if (!encabezadoDoc.exists()) {
                throw new Error(`No se encontró la cotización #${numCotizacion}`);
            }

            const encabezadoData = encabezadoDoc.data();
            const fechaData = encabezadoData.fecha;            
            let fechaDate = new Date(); // Fallback a la fecha actual
            
            if (fechaData && fechaData.toDate) {
                fechaDate = fechaData.toDate();
            } else if (typeof fechaData === 'string') {
                // Para compatibilidad con datos antiguos que puedan ser strings
                fechaDate = new Date(fechaData);
            }

            setEncabezado({
                ...encabezadoData,
                numCotizacion: numCotizacion,
                fecha: fechaDate
            });

            const detallesQuery = query(collection(db, 'SicCotizaDetalles'), where('numCotizacion', '==', numCotizacion));
            const detallesSnapshot = await getDocs(detallesQuery);
            const detallesList = detallesSnapshot.docs.map(doc => doc.data()).sort((a, b) => (a.orden || 0) - (b.orden || 0));
            
            if (detallesList.length > 0) {
                setDetalles(detallesList);
            } else {
                setDetalles([{ orden: 1, cantidad: 1, detalle: 'Descripción del artículo/servicio', precioUnitario: 0, subtotal: 0, impuesto: 0 }]);
            }

            setIsEditing(true);
            setSearchTerm('');
        } catch (err) {
            console.error("Error al cargar cotización:", err);
            setError(`Error al cargar: ${err.message}`);
            alert(`Error al cargar la cotización: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Buscar cotizaciones
    const handleSearch = async () => {
        const term = searchTerm.trim();
        if (!term) return;

        // Si es un número, buscar directamente
        if (/^\d+$/.test(term)) {
            await loadCotizacion(term.padStart(4, '0'));
            return;
        }

        // Si es texto, buscar por nombre de cliente
        try {
            const encabezadosSnapshot = await getDocs(collection(db, 'SicCotizaEncabezado'));
            const resultados = encabezadosSnapshot.docs
                .map(doc => ({
                    id: doc.id,
                    numCotizacion: doc.id,
                    cliente: doc.data().cliente || '',
                    fecha: doc.data().fecha,
                    total: doc.data().total || 0,
                    moneda: doc.data().moneda || 'Colones'
                }))
                .filter(cot => 
                    cot.cliente.toLowerCase().includes(term.toLowerCase())
                )
                .sort((a, b) => {
                    const numA = parseInt(a.numCotizacion) || 0;
                    const numB = parseInt(b.numCotizacion) || 0;
                    return numB - numA; // Más recientes primero
                });

            if (resultados.length === 0) {
                alert(`No se encontraron cotizaciones para "${term}"`);
                return;
            }

            if (resultados.length === 1) {
                await loadCotizacion(resultados[0].numCotizacion);
            } else {
                setSearchResults(resultados);
                setIsSearchModalOpen(true);
            }
        } catch (error) {
            console.error("Error al buscar:", error);
            alert("Error al buscar cotizaciones");
        }
    };

    // Navegación previo/siguiente
    const navigateCotizacion = (direction) => {
        if (allCotizaciones.length === 0) {
            alert("No hay cotizaciones disponibles para navegar");
            return;
        }

        const currentNum = encabezado.numCotizacion;
        if (!currentNum) {
            // Si no hay cotización actual, cargar la primera o última según dirección
            const target = direction === 'prev' ? allCotizaciones[allCotizaciones.length - 1] : allCotizaciones[0];
            loadCotizacion(target.numCotizacion);
            return;
        }

        const currentIndex = allCotizaciones.findIndex(c => c.numCotizacion === currentNum);
        if (currentIndex === -1) {
            alert("No se encontró la cotización actual en la lista");
            return;
        }

        let newIndex;
        if (direction === 'prev') {
            newIndex = currentIndex - 1;
            if (newIndex < 0) {
                alert("Ya estás en la primera cotización");
                return;
            }
        } else {
            newIndex = currentIndex + 1;
            if (newIndex >= allCotizaciones.length) {
                alert("Ya estás en la última cotización");
                return;
            }
        }

        loadCotizacion(allCotizaciones[newIndex].numCotizacion);
    };

    if (loading && usuarios.length === 0) {
        return <div className="loading-container"><div className="loading-spinner"></div><p>Cargando datos iniciales...</p></div>;
    }

    const currencySymbol = encabezado.moneda === 'Dólares' ? '$' : '₡';
    const currentUserEmailGlobal = auth.currentUser?.email;
    const currentUserGlobal = usuarios.find(u => u.email === currentUserEmailGlobal);
    const isAdmin = (currentUserGlobal?.rol || '').toLowerCase() === 'admin';

    return (
        <>
            {isCalculatorModalOpen && (
                <PriceCalculatorModal
                    onClose={() => setIsCalculatorModalOpen(false)}
                    onCalculate={handleCalculatePrice}
                    currencySymbol={currencySymbol}
                />
            )}

            {isClienteModalOpen && (
                <ClienteSearchModal
                    onClose={() => setIsClienteModalOpen(false)}
                    onSelect={handleClienteSelect}
                    isAdmin={isAdmin}
                />
            )}

            {/* Modal de resultados de búsqueda */}
            {isSearchModalOpen && (
                <div className="modal-overlay" onClick={() => setIsSearchModalOpen(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()} style={{ width: '80%', maxWidth: '700px' }}>
                        <div className="modal-header">
                            <h2>Resultados de Búsqueda</h2>
                            <button onClick={() => setIsSearchModalOpen(false)} className="modal-close">×</button>
                        </div>
                        <div className="modal-body">
                            <div className="table-responsive" style={{ maxHeight: '400px' }}>
                                <table className="usuarios-table" style={{ width: '100%' }}>
                                    <thead>
                                        <tr>
                                            <th style={{ width: '20%' }}># Cotización</th>
                                            <th style={{ width: '50%' }}>Cliente</th>
                                            <th style={{ width: '20%' }}>Total</th>
                                            <th style={{ width: '10%' }}>Acción</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {searchResults.map((cotizacion) => {
                                            const currencySymbol = (cotizacion.moneda ?? 'Colones') === 'Dólares' ? '$' : '₡';
                                            return (
                                                <tr key={cotizacion.id}>
                                                    <td>{cotizacion.numCotizacion}</td>
                                                    <td>{cotizacion.cliente}</td>
                                                    <td style={{ textAlign: 'right' }}>
                                                        {currencySymbol}{(cotizacion.total || 0).toLocaleString('en-US', {
                                                            minimumFractionDigits: 2,
                                                            maximumFractionDigits: 2
                                                        })}
                                                    </td>
                                                    <td>
                                                        <button
                                                            className="icon-btn success"
                                                            onClick={() => {
                                                                loadCotizacion(cotizacion.numCotizacion);
                                                                setIsSearchModalOpen(false);
                                                            }}
                                                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '5px' }}
                                                        >
                                                            ✅
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Barra Sticky */}
            <div className={"sticky-toolbar" + (isToolbarFixed ? " fixed" : "")} ref={toolbarRef} style={isToolbarFixed ? { top: headerHeight } : undefined}>
                <div className="sticky-toolbar__inner">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
                        <div className="tooltip-container">
                            <button 
                                className="icon-btn" 
                                onClick={() => navigateCotizacion('prev')} 
                                disabled={loading || !encabezado.numCotizacion}
                                style={{ backgroundColor: '#17a2b8', color: 'white' }}
                            >
                                ⬅️
                            </button>
                            <span className="tooltip-text">Cotización Anterior</span>
                        </div>
                        <div className="numero-cotizacion" style={{ margin: 0 }}>
                            <strong style={{ fontSize: '1.2rem', color: '#333' }}>📝 Cotización #: {encabezado.numCotizacion || '00000'}</strong>
                        </div>
                        <div className="tooltip-container">
                            <button 
                                className="icon-btn" 
                                onClick={() => navigateCotizacion('next')} 
                                disabled={loading || !encabezado.numCotizacion}
                                style={{ backgroundColor: '#17a2b8', color: 'white' }}
                            >
                                ➡️
                            </button>
                            <span className="tooltip-text">Cotización Siguiente</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: '1rem' }}>
                            <input
                                type="text"
                                placeholder="Buscar por # o cliente..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                style={{ 
                                    padding: '0.4rem 0.75rem', 
                                    border: '1px solid #ced4da', 
                                    borderRadius: '4px',
                                    fontSize: '0.9rem',
                                    width: '200px'
                                }}
                            />
                            <div className="tooltip-container">
                                <button 
                                    className="icon-btn" 
                                    onClick={handleSearch} 
                                    disabled={loading || !searchTerm.trim()}
                                    style={{ backgroundColor: '#ffc107', color: '#333' }}
                                >
                                    🔍
                                </button>
                                <span className="tooltip-text">Buscar</span>
                            </div>
                        </div>
                    </div>
                    <div className="toolbar-buttons">
                        <div className="tooltip-container">
                            <button className="icon-btn" onClick={handleNew} disabled={loading} style={{ backgroundColor: '#28a745', color: 'white' }}>
                                📄
                            </button>
                            <span className="tooltip-text">Nueva Cotización</span>
                        </div>
                        <div className="tooltip-container">
                            <button 
                                className="icon-btn" 
                                onClick={handleDuplicate} 
                                disabled={loading || !isEditing}>
                                ➕
                            </button>
                            <span className="tooltip-text">Duplicar Cotización</span>
                        </div>
                        <div className="tooltip-container">
                          
                            <span className="tooltip-text">Duplicar Cotización</span>
                        </div>
                        <div className="tooltip-container">
                            <button className="icon-btn" onClick={handleSave} disabled={loading} style={{ backgroundColor: '#007bff', color: 'white' }}>
                                💾
                            </button>
                            <span className="tooltip-text">{isEditing ? 'Actualizar Cotización' : 'Guardar Cotización'}</span>
                        </div>
                        <div className="tooltip-container">
                            <button 
                                className="icon-btn" 
                                onClick={async () => {
                                    if (!encabezado.numCotizacion) {
                                        alert('Debe guardar la cotización primero para generar el reporte.');
                                        return;
                                    }
                                    await abrirReporteCotizacion(encabezado.numCotizacion);
                                }} 
                                disabled={loading || !encabezado.numCotizacion} 
                                style={{ backgroundColor: '#6c757d', color: 'white' }}
                            >
                                📊
                            </button>
                            <span className="tooltip-text">Generar Reporte</span>
                        </div>
                    </div>
                </div>
            </div>
            {/* Espaciador para evitar saltos cuando se fija */}
            <div className={"sticky-toolbar__spacer" + (isToolbarFixed ? " active" : "")} style={{ height: isToolbarFixed ? toolbarHeight : 0 }}></div>

            <div className="gestion-usuarios-container cotizaciones-container"> {/* Añadido margin-top en CSS */}
                <div className="cotizacion-layout-vertical">
                    {error && <div className="alert alert-danger">{error}</div>}

                    {/* Panel Superior - Encabezado */}
                    <div className="panel-encabezado">
                        <div className="card">
                            <div className="card-body">
                                <div className="seccion-encabezado">
                                    <div className="form-grid">
                                        <div className="form-row form-row-half">
                                            <label className="form-label">Fecha:</label>
                                            <div className="fecha-display">
                                                {encabezado.fecha instanceof Date 
                                                ? `${['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'][encabezado.fecha.getDay()]}, ${encabezado.fecha.getDate()} de ${['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'][encabezado.fecha.getMonth()]} de ${encabezado.fecha.getFullYear()}`
                                                : getTodayFormatted()
                                                }
                                            </div>
                                        </div>
                                        
                                        <div className="form-row form-row-full">
                                            <label className="form-label">Cliente:</label>
                                            <div style={{ display: 'flex', flex: '1 1 auto', alignItems: 'center' }}>
                                                <input
                                                    type="text"
                                                    name="cliente"
                                                    value={encabezado.cliente}
                                                    onChange={handleEncabezadoChange}
                                                    className="form-control"
                                                    readOnly
                                                    style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed', width: '100%' }}
                                                />
                                                <div className="tooltip-container" style={{ marginLeft: '8px' }}>
                                                    <button
                                                        className="icon-btn"
                                                        type="button"
                                                        onClick={() => setIsClienteModalOpen(true)}
                                                        style={{ 
                                                            backgroundColor: '#bccbd8ff', 
                                                            color: 'white',
                                                            fontSize: '1.1rem'
                                                        }}
                                                    >
                                                        👨‍💼
                                                    </button>
                                                    <span className="tooltip-text">Seleccionar cliente</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="form-row form-row-full">
                                            <label className="form-label">Atención:</label>
                                            <input 
                                                type="text" 
                                                name="atencion" 
                                                value={encabezado.atencion} 
                                                onChange={handleEncabezadoChange} 
                                                className="form-control" 
                                            />
                                        </div>
                                        
                                        <div className="form-row form-row-full">
                                            <label className="form-label">Correo:</label>
                                            <input 
                                                type="email" 
                                                name="correo" 
                                                value={encabezado.correo} 
                                                onChange={handleEncabezadoChange} 
                                                className="form-control" 
                                            />
                                        </div>
                                        
                                        <div className="form-row">
                                            <label className="form-label">Tel. Oficina:</label>
                                            <input 
                                                type="tel" 
                                                name="telefonoOficina" 
                                                value={encabezado.telefonoOficina} 
                                                onChange={handleEncabezadoChange} 
                                                className="form-control telefono-input" 
                                                placeholder="9999-9999" 
                                                maxLength="9" 
                                            />
                                            <label className="form-label" style={{ marginLeft: '1rem' }}>Tel. Celular:</label>
                                            <input 
                                                type="tel" 
                                                name="telefonoCelular" 
                                                value={encabezado.telefonoCelular} 
                                                onChange={handleEncabezadoChange} 
                                                className="form-control telefono-input" 
                                                placeholder="9999-9999" 
                                                maxLength="9" 
                                            />
                                        </div>
                                        
                                        <div className="form-row form-row-full">
                                            <label className="form-label">Observaciones:</label>
                                            <textarea
                                                name="observaciones"
                                                value={encabezado.observaciones}
                                                onChange={handleEncabezadoChange}
                                                className="form-control"
                                                rows="2"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="separador"></div>

                                <div className="seccion-moneda">
                                    <div className="radio-group">
                                        <div className="form-check">
                                            <input 
                                                className="form-check-input" 
                                                type="radio" 
                                                name="moneda" 
                                                value="Colones" 
                                                checked={encabezado.moneda === 'Colones'} 
                                                onChange={handleEncabezadoChange} 
                                                id="colonesRadio" 
                                            />
                                            <label className="form-check-label" htmlFor="colonesRadio">
                                                Colones
                                            </label>
                                        </div>
                                        <div className="form-check">
                                            <input 
                                                className="form-check-input" 
                                                type="radio" 
                                                name="moneda" 
                                                value="Dólares" 
                                                checked={encabezado.moneda === 'Dólares'} 
                                                onChange={handleEncabezadoChange} 
                                                id="dolaresRadio" 
                                            />
                                            <label className="form-check-label" htmlFor="dolaresRadio">
                                                Dólares
                                            </label>
                                        </div>
                                        <div className="form-check">
                                            <input 
                                                className="form-check-input" 
                                                type="checkbox" 
                                                name="exento" 
                                                checked={encabezado.exento} 
                                                onChange={handleEncabezadoChange} 
                                                id="exentoCheck" 
                                            />
                                            <label className="form-check-label" htmlFor="exentoCheck">
                                                Exento
                                            </label>
                                        </div>
                                    </div>
                                    
                                    <div className="config-grid-compact">
                                        <div className="form-row compact">
                                            <label className="form-label">Tipo Cambio:</label>
                                            <input 
                                                type="number" 
                                                name="tipoCambio" 
                                                value={encabezado.tipoCambio} 
                                                onChange={handleEncabezadoChange} 
                                                className="form-control narrow-input" 
                                                disabled={encabezado.moneda === 'Dólares'} 
                                            />
                                        </div>
                                        <div className="form-row compact">
                                            <label className="form-label">% Utilidad:</label>
                                            <input 
                                                type="number" 
                                                name="porcentajeUtilidad" 
                                                value={encabezado.porcentajeUtilidad} 
                                                onChange={handleEncabezadoChange} 
                                                className="form-control narrow-input" 
                                            />
                                        </div>
                                        <div className="form-row compact">
                                            <label className="form-label">Forma Pago:</label>
                                            <select 
                                                name="formaDePago" 
                                                value={encabezado.formaDePago} 
                                                onChange={handleEncabezadoChange} 
                                                className="form-select forma-pago-input"
                                            >
                                                <option value="Contado">Contado</option>
                                                <option value="Crédito">Crédito</option>
                                                <option value="Otro">Otro</option>
                                            </select>
                                        </div>
                                        <div className="form-row compact">
                                            <label className="form-label">Plazo:</label>
                                            <input 
                                                type="number" 
                                                name="plazo" 
                                                value={encabezado.plazo} 
                                                onChange={handleEncabezadoChange} 
                                                className="form-control narrow-input" 
                                                disabled={encabezado.formaDePago !== 'Crédito'} 
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="separador"></div>

                                <div className="seccion-firma-compact">
                                    <div className="form-row">
                                      
                                        <label className="form-label">Hecho Por:</label>
                                        <select 
                                            name="hechopor" 
                                            value={encabezado.hechopor} 
                                            onChange={handleEncabezadoChange} 
                                            className="form-select hecho-por-input"
                                        >
                                            <option value="">Seleccione...</option>
                                            {usuarios.map(u => <option key={u.id} value={u.nombre}>{u.nombre}</option>)}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Panel Inferior - Detalles y Totales */}
                    <div className="panel-detalles">
                        <div className="card">
                            <div className="card-body">
                                <div className="seccion-detalles">
                                    <h3 className="seccion-titulo">Artículos/Servicios</h3>
                                    
                                    <div className="table-container">
                                        <table className="cotizacion-table">
                                            <thead>
                                                <tr className="table-header-row">
                                                    <th onClick={handleSortByOrden} className="sortable-header" style={{ width: '6%' }}># {sortOrder === 'asc' ? '▲' : '▼'}</th>
                                                    <th className="text-end" style={{ width: '11%' }}>Cantidad</th>
                                                    <th style={{ width: '50%' }}>Detalle</th>
                                                    <th className="text-end" style={{ width: '15%' }}>Unitario</th>
                                                    <th className="text-end" style={{ width: '15%' }}>Total</th>
                                                    <th style={{ width: '8%' }}></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {detalles.map((item, index) => (
                                                    <tr key={index}>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                value={item.orden}
                                                                onChange={(e) => handleOrdenChange(index, e.target.value)}
                                                                onBlur={sortDetalles}
                                                                className="form-control text-center"
                                                                style={{ width: '50px' }}
                                                            />
                                                        </td>
                                                        <td>
                                                            <input 
                                                                type="number" 
                                                                name="cantidad" 
                                                                value={item.cantidad} 
                                                                onChange={(e) => handleDetalleChange(index, e)}
                                                                className="form-control text-end cantidad-input"
                                                                min="1" 
                                                                step="0.01"
                                                            />
                                                        </td>
                                                        <td>
                                                            <input 
                                                                type="text" 
                                                                name="detalle" 
                                                                value={item.detalle} 
                                                                onChange={(e) => handleDetalleChange(index, e)} 
                                                                className="form-control" 
                                                            />
                                                        </td>
                                                        <td>
                                                            <input 
                                                                type="number" 
                                                                name="precioUnitario" 
                                                                value={item.precioUnitario} 
                                                                onChange={(e) => handleDetalleChange(index, e)} 
                                                                className="form-control text-end" 
                                                                min="0" 
                                                                step="0.01" 
                                                            />
                                                        </td>
                                                        <td className="text-end">
                                                            {`${currencySymbol}${item.subtotal.toLocaleString('en-US', { 
                                                                minimumFractionDigits: 2,
                                                                maximumFractionDigits: 2 
                                                            })}`}
                                                        </td>
                                                        <td>
                                                            <div style={{ display: 'flex', justifyContent: 'center', gap: '5px' }}>
                                                                <div className="tooltip-container">
                                                                    <button 
                                                                        className="icon-btn" 
                                                                    onClick={() => openCalculatorModal(index)} 
                                                                        title="Calcular Precio Unitario"
                                                                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '5px', fontSize: '1.2rem' }}
                                                                    >
                                                                        🧮
                                                                    </button>
                                                                    <span className="tooltip-text">Calcular Precio</span>
                                                                </div>
                                                                <div className="tooltip-container">
                                                                    <button 
                                                                        className="icon-btn danger" 
                                                                        onClick={() => removeDetalle(index)} 
                                                                        title="Eliminar línea" 
                                                                        disabled={detalles.length === 1}
                                                                        style={{ 
                                                                            background: 'none', 
                                                                            border: 'none', 
                                                                            cursor: 'pointer', 
                                                                            padding: '5px',
                                                                            fontSize: '1.2rem'
                                                                        }}
                                                                    >
                                                                        🗑️
                                                                    </button>
                                                                    <span className="tooltip-text">Eliminar Línea</span>
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    
                                    <button className="btn btn-secondary btn-sm agregar-linea" onClick={addDetalle}>
                                        + Agregar Línea
                                    </button>
                                </div>
                                
                                <div className="separador"></div>
                                
                                <div className="seccion-totales">
                                    <div className="total-row">
                                        <span className="total-label">Sub Total:</span>
                                        <span className="total-value">{`${currencySymbol}${totales.subtotal.toLocaleString('en-US', { 
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2 
                                        })}`}</span>
                                    </div>
                                    <div className="total-row">
                                        <span className="total-label">IVA ({encabezado.iva}%):</span>
                                        <span className="total-value">{`${currencySymbol}${totales.impuesto.toLocaleString('en-US', { 
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2 
                                        })}`}</span>
                                    </div>
                                    <div className="total-row total-final">
                                        <span className="total-label">Total:</span>
                                        <span className="total-value">{`${currencySymbol}${totales.total.toLocaleString('en-US', { 
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2 
                                        })}`}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default SicCotizaciones;